import React, { useContext, useEffect, useRef, useState } from 'react';

import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip
} from '@mui/material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import { useLocation, useNavigate } from 'react-router-dom';

import { Alerta } from '../../components/alerta';
import DadosUsuario from '../../utils/context';
import { downloadBlob } from '../../utils/csvExport';
import { formatIsoDate, isValidDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/stringFormat';
import { isPostSaleProfile } from '../../utils/userProfile';
import CadastrarLeadCRM from '../comercial/cadastrarLead';
import '../css/crmDashboard.css';
import '../css/crmLeads.css';
import {
    formatLeadColumnValue,
    getLeadPhaseToneClass,
    getLeadStatusToneClass,
    LEAD_COLUMNS
} from './crmShared';
import { CrmLeadsFilters } from './crmLeadsFilters';
import {
    CRM_LEADS_INITIAL_DATE_FILTERS,
    CRM_LEADS_INITIAL_FILTERS,
    CRM_LEAD_DATE_FILTER_RANGES,
    CRM_LEAD_DEBOUNCED_FILTER_FIELDS,
    CRM_LEAD_INTEGER_FILTER_FIELDS,
    CRM_LEAD_SELECT_FILTER_FIELDS,
    CRM_LEAD_PHASE_LOCKED_HIDDEN_FIELDS,
    CRM_LEAD_TICKET_ORDER_FIELD,
    CRM_LEAD_WON_PHASE_ID,
    fetchCrmLeadsPage,
    fetchCrmLeadsReport
} from './crmLeadsData';

const buildDateFilterPayload = (dateFilters) => Object.values(CRM_LEAD_DATE_FILTER_RANGES)
    .reduce((payload, range) => {
        const startDate = dateFilters[range.start];
        const endDate = dateFilters[range.end];
        const isCompleteValidRange = isValidDate(startDate) &&
            isValidDate(endDate) &&
            startDate.getTime() <= endDate.getTime();

        payload[range.start] = isCompleteValidRange ? formatIsoDate(startDate) : '';
        payload[range.end] = isCompleteValidRange ? formatIsoDate(endDate) : '';
        return payload;
    }, {});

const renderLeadCellContent = (column, row) => {
    const value = formatLeadColumnValue(column, row[column.field]);

    if (column.field === 'status') {
        return (
            <span className={`crm-leads-tone-pill crm-leads-status-pill ${getLeadStatusToneClass(row.status)}`}>
                {value}
            </span>
        );
    }

    if (column.field === 'fase') {
        return (
            <span className={`crm-leads-tone-pill crm-leads-phase-pill ${getLeadPhaseToneClass(row.fase)}`}>
                {value}
            </span>
        );
    }

    if (column.field === 'nome') {
        return <span className='crm-leads-name-cell'>{value}</span>;
    }

    if (column.field === 'ticket') {
        return <span className='crm-leads-money-cell'>{value}</span>;
    }

    return value;
};

const CrmLeads = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initialListState = useRef(location.state?.leadListState ?? null);
    const { autorizacoes = [] } = useContext(DadosUsuario);
    const isPhaseLocked = isPostSaleProfile(autorizacoes);
    const lockedPhaseFilter = isPhaseLocked ? { fase_id: CRM_LEAD_WON_PHASE_ID } : {};
    const visibleColumns = isPhaseLocked
        ? LEAD_COLUMNS.filter((column) => !CRM_LEAD_PHASE_LOCKED_HIDDEN_FIELDS.includes(column.field))
        : LEAD_COLUMNS;
    const initialFilters = useRef({
        ...CRM_LEADS_INITIAL_FILTERS,
        ...(initialListState.current?.filters ?? {}),
        ...lockedPhaseFilter
    });
    const [filters, setFilters] = useState(initialFilters.current);
    const [debouncedFilters, setDebouncedFilters] = useState(initialFilters.current);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [totalTicket, setTotalTicket] = useState(null);
    const [page, setPage] = useState(initialListState.current?.page ?? 1);
    const [pageSize, setPageSize] = useState(initialListState.current?.pageSize ?? 50);
    const [dateFilters, setDateFilters] = useState(initialListState.current?.dateFilters ?? CRM_LEADS_INITIAL_DATE_FILTERS);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [openCreateLead, setOpenCreateLead] = useState(false);
    const [leadListRefreshKey, setLeadListRefreshKey] = useState(0);
    const [message, setMessage] = useState('');
    const [statusAlerta, setStatusAlerta] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const hasMountedDebounce = useRef(false);
    const isPageLoading = isLoading || isFetching;
    const canViewMarketingFilters = autorizacoes.includes('Adm') || autorizacoes.includes('MKT');

    useEffect(() => {
        if (!hasMountedDebounce.current) {
            hasMountedDebounce.current = true;
            return undefined;
        }

        const nextDebouncedFilters = CRM_LEAD_DEBOUNCED_FILTER_FIELDS.reduce((acc, field) => {
            acc[field] = filters[field];
            return acc;
        }, {});

        const timeoutId = window.setTimeout(() => {
            setDebouncedFilters((prev) => ({ ...prev, ...nextDebouncedFilters }));
        }, 1000);

        return () => window.clearTimeout(timeoutId);
    }, [filters.campaign, filters.cliente_id, filters.content, filters.cpf, filters.cupom, filters.email, filters.lead_id, filters.nome, filters.origem, filters.source, filters.telefone, filters.term]);

    useEffect(() => {
        const loadLeads = async () => {
            setIsLoading(!hasLoaded);
            setIsFetching(hasLoaded);

            try {
                const data = await fetchCrmLeadsPage({
                    dateFilters: buildDateFilterPayload(dateFilters),
                    filters: debouncedFilters,
                    page,
                    pageSize
                });

                setRows(data.items);
                setTotalRows(data.total);
                setTotalTicket(data.totalTicket);
                setPage(data.page);
                setHasLoaded(true);
            } catch (error) {
                setMessage(error.message);
                setStatusAlerta('error');
                setOpenAlerta(true);
            } finally {
                setIsLoading(false);
                setIsFetching(false);
            }
        };

        loadLeads();
    }, [dateFilters, debouncedFilters, leadListRefreshKey, page, pageSize]);

    const handleFilterChange = (field, value) => {
        setPage(1);
        const nextValue = Array.isArray(value)
            ? value.map(Number)
            : CRM_LEAD_INTEGER_FILTER_FIELDS.includes(field) || field === CRM_LEAD_TICKET_ORDER_FIELD
                ? (value === '' ? null : Number(value))
                : CRM_LEAD_SELECT_FILTER_FIELDS.includes(field) && value !== '' ? Number(value) : value;

        setFilters((prev) => ({ ...prev, [field]: nextValue }));

        if (CRM_LEAD_SELECT_FILTER_FIELDS.includes(field) || field === CRM_LEAD_TICKET_ORDER_FIELD) {
            setDebouncedFilters((prev) => ({ ...prev, [field]: nextValue }));
        }
    };

    const handleClearFilters = () => {
        const clearedFilters = { ...CRM_LEADS_INITIAL_FILTERS, ...lockedPhaseFilter };

        setFilters(clearedFilters);
        setDebouncedFilters(clearedFilters);
        setDateFilters(CRM_LEADS_INITIAL_DATE_FILTERS);
        setPage(1);
    };

    const handleDateRangeApply = (range, startDate, endDate) => {
        setDateFilters((prev) => ({
            ...prev,
            [range.start]: startDate,
            [range.end]: endDate
        }));
        setPage(1);
    };

    const showAlert = (text, severity = 'info') => { setMessage(text); setStatusAlerta(severity); setOpenAlerta(true); };

    const handleExportCsv = async () => {
        setIsGeneratingReport(true);

        try {
            const response = await fetchCrmLeadsReport({
                dateFilters: buildDateFilterPayload(dateFilters),
                filters: debouncedFilters,
                page,
                pageSize
            });

            if (response instanceof Blob) {
                downloadBlob('leads.xlsx', response);
                showAlert('Relatório Excel gerado com sucesso.', 'success');
                return;
            }

            showAlert(response.data ?? response.message, response.status);
        } finally {
            setIsGeneratingReport(false);
        }
    };

    const handleCreateLeadSuccess = (response) => {
        setOpenCreateLead(false);
        setPage(1);
        setLeadListRefreshKey((prev) => prev + 1);
        showAlert(response.message, response.status);
    };

    return (
        <Box className='mainBox crm-leads-page'>
            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />

            <Backdrop open={isPageLoading} className='crm-leads-backdrop'>
                <CircularProgress color='inherit' />
                <p className='crm-leads-backdrop-text'>Carregando dados...</p>
            </Backdrop>

            <Dialog
                open={isGeneratingReport}
                disableEscapeKeyDown
                className='crm-leads-report-dialog'
                aria-labelledby='crm-leads-report-dialog-title'
            >
                <DialogContent className='crm-leads-report-dialog-content'>
                    <CircularProgress size={34} />
                    <div>
                        <strong id='crm-leads-report-dialog-title'>Gerando relatório</strong>
                        <span>Aguarde enquanto preparamos o arquivo CSV.</span>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog
                open={openCreateLead}
                onClose={() => setOpenCreateLead(false)}
                maxWidth='md'
                fullWidth
                className='crm-leads-create-dialog'
            >
                <DialogContent className='crm-leads-create-dialog-content'>
                    <CadastrarLeadCRM
                        onCancel={() => setOpenCreateLead(false)}
                        onSuccess={handleCreateLeadSuccess}
                    />
                </DialogContent>
            </Dialog>

            <div className='crm-leads-shell'>
                {hasLoaded && (
                    <Paper className='crm-leads-workspace'>
                        <div className='crm-leads-table-toolbar'>
                            <div>
                                <h2 className='crm-leads-section-title'>Base de leads</h2>
                                <p className='crm-leads-section-copy'>{totalRows} leads encontrados</p>
                            </div>

                            <div className='crm-leads-toolbar-actions'>
                                <div className='crm-leads-actions'>
                                    <Button className='crm-leads-button crm-leads-button-secondary' startIcon={<DashboardRoundedIcon />} onClick={() => navigate('/CRM/dashboard')}>
                                        Dashboard
                                    </Button>
                                    <Button className='crm-leads-button crm-leads-button-primary' startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => setOpenCreateLead(true)}>
                                        Novo Lead
                                    </Button>
                                    <Button
                                        className='crm-leads-button crm-leads-button-secondary'
                                        startIcon={<DownloadRoundedIcon />}
                                        onClick={handleExportCsv}
                                        disabled={isGeneratingReport}
                                    >
                                        Exportar
                                    </Button>
                                </div>

                                {totalTicket !== null && (
                                    <div className='crm-leads-ticket-summary'>
                                        <span>Ticket total do filtro</span>
                                        <strong>{formatCurrency(totalTicket)}</strong>
                                    </div>
                                )}
                            </div>
                        </div>

                        <TableContainer className='crm-leads-table-shell'>
                            <Table size='small' className='crm-leads-table' aria-label='Tabela de leads'>
                                <TableHead className='crm-leads-table-head'>
                                    <TableRow>
                                        <TableCell className='crm-leads-head-cell crm-leads-actions-cell' aria-label='Ações' />
                                        {visibleColumns.map((column) => (
                                            <TableCell
                                                key={column.field}
                                                className='crm-leads-head-cell'
                                                data-field={column.field}
                                            >
                                                {column.headerName}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    <CrmLeadsFilters
                                        filters={filters}
                                        onFilterChange={handleFilterChange}
                                        dateFilters={dateFilters}
                                        canViewMarketingFilters={canViewMarketingFilters}
                                        columns={visibleColumns}
                                        onDateRangeApply={handleDateRangeApply}
                                        onClearFilters={handleClearFilters}
                                    />
                                </TableHead>

                                <TableBody>
                                    {rows.map((row, index) => (
                                        <TableRow key={`${row.lead_id}-${page}-${index}`} hover className='crm-leads-row'>
                                            <TableCell className='crm-leads-cell crm-leads-actions-cell'>
                                                <Tooltip title='Editar' arrow>
                                                    <button
                                                        type='button'
                                                        className='crm-leads-edit-button'
                                                        aria-label={`Editar lead ${row.lead_id}`}
                                                        onClick={() => navigate(`/CRM/leads/${row.lead_id}`, {
                                                            state: {
                                                                leadData: row,
                                                                leadListState: {
                                                                    filters,
                                                                    dateFilters,
                                                                    page,
                                                                    pageSize
                                                                }
                                                            }
                                                        })}
                                                    >
                                                        <EditRoundedIcon fontSize='inherit' />
                                                    </button>
                                                </Tooltip>
                                            </TableCell>
                                            {visibleColumns.map((column) => (
                                                <TableCell
                                                    key={column.field}
                                                    className='crm-leads-cell'
                                                    data-field={column.field}
                                                >
                                                    {renderLeadCellContent(column, row)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}

                                    {rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={visibleColumns.length + 1} align='center' className='crm-leads-feedback-cell'>
                                                Nenhum lead para os filtros aplicados.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <div className='crm-leads-pagination-shell'>
                            <div className='crm-leads-pagination-info'>
                                <span className='crm-leads-pagination-count'>
                                    Página {page} de {Math.max(1, Math.ceil(totalRows / pageSize))}
                                </span>
                                <button
                                    type='button'
                                    className='crm-leads-clear-all-button'
                                    onClick={handleClearFilters}
                                >
                                    <FilterListRoundedIcon fontSize='inherit' />
                                    <span>Limpar todos os filtros</span>
                                </button>
                            </div>
                            <TablePagination
                                component='div'
                                count={totalRows}
                                page={page - 1}
                                onPageChange={(event, newPage) => setPage(newPage + 1)}
                                rowsPerPage={pageSize}
                                onRowsPerPageChange={(event) => {
                                    setPageSize(parseInt(event.target.value, 10));
                                    setPage(1);
                                }}
                                rowsPerPageOptions={[25, 50, 100]}
                                labelRowsPerPage='Leads por página'
                            />
                        </div>
                    </Paper>
                )}
            </div>
        </Box>
    );
};

export default CrmLeads;
